import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  listFilePriceEntitlements,
  ProductInBasket,
  ShoppingCartState,
} from '../../../shopping-cart-back/actions/standalone';
import {
  selectFavList,
  selectFilePrices,
  selectFilesInBasket,
  selectPackagesInBasket,
  selectPhotoGroups,
  selectPhotoGroupsHasPhoto,
  selectPhotosList,
  selectPriceToAddToEveryFileInProduct,
  selectPriceToAddToFirstPrint,
  selectPrintsInBasket,
  selectProductsInBasket,
  selectSettingPhotoGroup,
} from '../../../shopping-cart-back/actions/selector';
import { ZalamoObject } from '../../../../../zalamo/pipes/names/graphql';
import { map } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FullscreenService } from 'ZalamoLibrary/src/lib/ui/fullscreen/fullscreen.service';
import { TooltipRef } from 'ZalamoLibrary/src/lib/ui/zalamo-tooltip/tooltip-ref';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { TypedFormBuilder } from 'ZalamoLibrary/src/lib/ui/form/TypedFormBuilder';
import { PriceConfig } from '../../../../../../../../photographer-client/src/app/modules/private-sessions/graphql';
import { singleStepPriceCalculator } from '@ui/step-pricing/StepPriceCalculator';
import { StepPricingMode } from '../../../../../../../../photographer-panel/src/graphql';
import { translationProvider } from '@ui/nls/translations/translate.directive';
import { ENFORCE_SCREEN_PROTECTION_IN_NATIVE_APP } from '@ui/zalamo-dialog/screen-protection.token';

@Component({
    selector: 'lib-missing-photos-list',
    templateUrl: './missing-photos-list.component.html',
    styleUrls: ['./missing-photos-list.component.scss'],
    providers: [translationProvider('productFront')],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MissingPhotosListComponent implements OnInit {
  static readonly [ENFORCE_SCREEN_PROTECTION_IN_NATIVE_APP] = true;
  photos$ = this.store.pipe(select(selectPhotosList));
  photoGroupsHasPhoto$ = this.store.pipe(select(selectPhotoGroupsHasPhoto));
  favList$ = this.store.pipe(select(selectFavList));
  photoId = '';
  isPickerOnly = false;
  checked$ = new BehaviorSubject<boolean>(false);
  check = this.fb.group({ checked: false });
  @Input() photoIdList: Array<{ photoId: string }> = [];
  @Input() maxPhoto = 1;
  @Input() selectedProductId: string;
  @Input() quantity = 1;
  @Input() photosSelectedByPhotographer: Array<{ photoId: string }> = [];
  @Input() swap = false;
  @Input() product;
  @Input() configuration = new BehaviorSubject<{
    [x: string]: string | boolean | number;
  }>({});
  @Output() closeDialog = new EventEmitter();
  @Output() addProduct = new EventEmitter();
  @Output() swapProduct = new EventEmitter();
  croppedPhotos = false;
  @Input() @Required context: ZalamoObject;
  @Input() @Required contextId: string;
  tooltipRef: TooltipRef<any> = null;
  @Input() productNotPackage = false;
  @Input() firstAddedFile = false;
  priceToAddToEveryFileInProduct$ = this.store.pipe(
    select(selectPriceToAddToEveryFileInProduct),
  );
  photoIdList$ = new BehaviorSubject<Array<{ photoId: string }>>([]);
  photosList$: Observable<
    Array<{
      height: number;
      width: number;
      photoGroupHasPhotoId: string;
      photoId: string;
      photoGroupId: string;
      surcharge: { price: Array<PriceConfig>; surcharge: boolean };
      check: boolean;
    }>
  > = combineLatest([
    this.store.pipe(select(selectProductsInBasket)),
    this.store.pipe(select(selectPriceToAddToEveryFileInProduct)),
    this.store.pipe(select(selectPhotosList)),
    this.store.pipe(select(selectPhotoGroups)),
    combineLatest([
      this.store.pipe(select(selectFilesInBasket)),
      this.store.pipe(select(selectFilePrices)),
      this.store.pipe(select(selectPriceToAddToFirstPrint)),
      this.store.pipe(select(selectPrintsInBasket)),
      this.store.pipe(select(selectPackagesInBasket)),
    ]),
    combineLatest([
      this.photos$,
      this.photoIdList$,
      this.favList$,
      this.checked$,
      this.store.pipe(select(selectSettingPhotoGroup)),
    ]),
  ]).pipe(
    map(
      ([
        productsInBasket,
        priceToAddToEveryFileInProduct,
        photoGroupList,
        photoGroups,
        [
          filesInBasket,
          filePrices,
          priceToAddToFirstPrint,
          printsInBasket,
          packagesInBasket,
        ],
        [photos, photoIdList, favList, checked, settingPhotoGroup],
      ]) => {
        const productInBasket = productsInBasket.find(
          ({ selectedProductId }) =>
            selectedProductId === this.selectedProductId,
        );
        const currentProductPhotoIdList = [
          ...this.photoIdList,
          ...photoIdList,
        ].filter(
          ({ photoId }, index, list) =>
            list.findIndex(({ photoId: id }) => id === photoId) === index,
        );
        const projectedProductTime =
          productInBasket?.timeAddedToShoppingCart ??
          Math.max(
            0,
            ...filesInBasket.map(
              ({ timeAddedToShoppingCart }) => timeAddedToShoppingCart ?? 0,
            ),
            ...printsInBasket.map(
              ({ timeAddedToShoppingCart }) => timeAddedToShoppingCart ?? 0,
            ),
            ...productsInBasket.map(
              ({ timeAddedToShoppingCart }) => timeAddedToShoppingCart ?? 0,
            ),
          ) + 1;
        const photosList: Array<{
          height: number;
          width: number;
          photoGroupHasPhotoId: string;
          photoId: string;
          photoGroupId: string;
          surcharge: { price: Array<PriceConfig>; surcharge: boolean };
          check: boolean;
        }> = [];
        this.photosSelectedByPhotographer
          .filter(({ photoId }) => {
            return !checked || favList.current.includes(photoId);
          })
          .forEach(({ photoId }) => {
            const photo = photos
              .find((x) => x.photos.find((y) => y.photoId === photoId))
              .photos.find((y) => y.photoId === photoId);
            const photoGroup = settingPhotoGroup.find(
              ({ photoGroupId }) => photoGroupId === photo.photoGroupId,
            );
            const config = photoGroups.find(
              ({ photoGroupId }) =>
                photoGroupId ===
                photoGroupList
                  .find(({ photos: photoList }) =>
                    photoList.find((p) => p.photoId === photoId),
                  )
                  .photos.find((p) => p.photoId === photoId).photoGroupId,
            )?.config;
            const supplement =
              config?.individualSellSettings &&
              config.sellProducts &&
              config.customProductsConfig
                ? config.priceToAddToEveryFileInProduct
                : priceToAddToEveryFileInProduct;
            const check = !!(
              this.photoIdList.find(({ photoId: pId }) => pId === photoId) ||
              photoIdList.find(({ photoId: pId }) => pId === photoId)
            );
            const filesPrice =
              config?.individualSellSettings && config.customFilesPricing
                ? config.files
                : filePrices;
            if (!filesPrice) {
              return;
            }
            const projectedPhotoIdList = check
              ? currentProductPhotoIdList
              : [...currentProductPhotoIdList, { photoId }];
            const projectedProduct: ProductInBasket = productInBasket
              ? { ...productInBasket, photoIdList: projectedPhotoIdList }
              : {
                  nameClientProduct: '',
                  productId:
                    this.product?.productId ?? '__product-price-preview__',
                  selectedProductId:
                    this.selectedProductId ?? '__product-price-preview__',
                  quantity: this.quantity,
                  configuration: this.configuration.value,
                  timeAddedToShoppingCart: projectedProductTime,
                  photoIdList: projectedPhotoIdList,
                  finished: { allImages: false, allOptions: false },
                  redBackground: false,
                  firstAddedFile: this.firstAddedFile,
                };
            const projectedProductsInBasket = productInBasket
              ? productsInBasket.map((product) =>
                  product.selectedProductId ===
                  productInBasket.selectedProductId
                    ? projectedProduct
                    : product,
                )
              : [...productsInBasket, projectedProduct];
            const projectedProductIndex =
              projectedProductsInBasket.indexOf(projectedProduct);
            const hasEarlierProductWithPhoto = projectedProductsInBasket.some(
              (product, index) =>
                index < projectedProductIndex &&
                product.photoIdList.some(({ photoId: id }) => id === photoId),
            );
            const filePriceEntitlements = listFilePriceEntitlements({
              photoGroups,
              photoGroupList,
              priceToAddToFirstPrint,
              priceToAddToEveryFileInProduct,
              filePrices,
              printsInBasket,
              filesInBasket,
              productsInBasket: projectedProductsInBasket,
              packagesInBasket,
            });
            const productEntitlement = hasEarlierProductWithPhoto
              ? undefined
              : filePriceEntitlements.find(
                  (entitlement) =>
                    entitlement.photoId === photoId &&
                    entitlement.type === 'product',
                );
            const pricingList = productEntitlement
              ? filePriceEntitlements.filter(
                  ({ filePricingId }) =>
                    filePricingId === productEntitlement.filePricingId,
                )
              : [];
            const productPriceIndex = pricingList.findIndex(
              (entitlement) =>
                entitlement.photoId === photoId &&
                entitlement.type === 'product',
            );
            const filePrice = filesPrice.find(
              ({ filePricingId }) =>
                filePricingId === productEntitlement?.filePricingId,
            );
            const shouldApplyProductSupplement =
              !hasEarlierProductWithPhoto &&
              supplement.addPriceToEveryFileInProduct &&
              !(
                (supplement.skipAddToEveryFileInProductIfFileBought &&
                  filesInBasket.some(
                    (file) =>
                      file.photoId === photoId && !file.togetherWithProduct,
                  )) ||
                (!supplement.skipAddToEveryFileInProductIfFileBought &&
                  filesInBasket.some(
                    (file) =>
                      file.photoId === photoId &&
                      !!file.togetherWithPrint &&
                      !!file.togetherWithLookBookProduct,
                  ))
              );
            const price = shouldApplyProductSupplement
              ? supplement.countWithFilePrices
                ? productPriceIndex >= 0 && filePrice
                  ? singleStepPriceCalculator(
                      productPriceIndex + 1,
                      pricingList.length,
                      !filePrice.stepPricingEnabled ||
                        filePrice.stepPricing.mode === 'None'
                        ? {
                            ...filePrice.stepPricing,
                            mode: StepPricingMode.None,
                          }
                        : filePrice.stepPricing,
                      filePrice.price,
                    )
                  : []
                : supplement.price
              : [];
            photosList.push({
              ...photo,
              surcharge: {
                price,
                surcharge:
                  this.productNotPackage && shouldApplyProductSupplement,
              },
              check,
            });
          });
        return photosList;
      },
    ),
  );
  isPhonePortrait$ = this.screenObserver
    .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
    .pipe(map(({ matches }) => matches));

  @ViewChild('messageAboutMaximumPhotos')
  messageAboutMaximumPhotos;
  @ViewChild('messageAboutTooManyPhotos')
  messageAboutTooManyPhotos;
  @ViewChild('messageAboutSurchargeForFile')
  messageAboutSurchargeForFile;
  @ViewChild('messageAboutNoMatchingFavoritePhotos')
  messageAboutNoMatchingFavoritePhotos;
  gridItemSize = this.screenObserver.isMatched(Breakpoints.Handset)
    ? Math.floor(screen.width - 34) / 3
    : 380;

  constructor(
    private fb: TypedFormBuilder,
    private screenObserver: BreakpointObserver,
    private store: Store<{ shoppingCart: ShoppingCartState }>,
    private fullscreenService: FullscreenService,
  ) {}

  ngOnInit() {}

  viewButton(photoId) {
    return (this.photoId = photoId);
  }

  closeViewButton() {
    return (this.photoId = '');
  }

  trackPhoto(i, { photoGroupHasPhotoId }) {
    return photoGroupHasPhotoId;
  }

  checkPhoto(photoId) {
    if (this.photoIdList.findIndex((value) => value.photoId === photoId) >= 0) {
      const photoIdList: Array<{ photoId: string }> = cloneDeep(
        this.photoIdList,
      );
      photoIdList.splice(
        this.photoIdList.findIndex((value) => value.photoId === photoId),
        1,
      );
      this.photoIdList = photoIdList;
    } else {
      switch (true) {
        case this.photoIdList.length < this.maxPhoto && this.maxPhoto !== 1:
          const photoIdList: Array<{ photoId: string }> = cloneDeep(
            this.photoIdList,
          );
          photoIdList.push({ photoId });
          this.photoIdList = photoIdList;
          break;
        case this.maxPhoto === 1:
          this.photoIdList = [];
          this.photoIdList.push({ photoId });
          break;
        case this.photoIdList.length >= this.maxPhoto && this.maxPhoto !== 1:
          const ref = this.fullscreenService.open<any, { response: number }>({
            content:
              this.photoIdList.length === this.maxPhoto
                ? this.messageAboutMaximumPhotos
                : this.messageAboutTooManyPhotos,
            data: {
              mode: 'warn',
            },
          });
          setTimeout(() => {
            ref.close();
          }, 3000);
          break;
      }
    }
    this.photoIdList$.next(this.photoIdList);
  }

  swapPhoto() {
    if (this.photoIdList.length <= this.maxPhoto) {
      this.swapProduct.emit(this.photoIdList);
    } else {
      const ref = this.fullscreenService.open<any, { response: number }>({
        content: this.messageAboutTooManyPhotos,
        data: {
          mode: 'warn',
        },
      });
      setTimeout(() => {
        ref.close();
      }, 3000);
    }
  }

  changeList() {
    const checked = !this.checked$.value;
    this.checked$.next(checked);
  }

  openMessengerAboutNoMatchingFavoritePhotos() {
    const ref = this.fullscreenService.open<any, { response: number }>({
      content: this.messageAboutNoMatchingFavoritePhotos,
      data: {
        mode: 'warn',
      },
    });
    setTimeout(() => {
      ref.close();
    }, 3000);
  }
}
